import React, {useRef, useState} from "react";
import {Button, Col, Drawer, Form, InputNumber, Radio, Row, Space, Tooltip} from "antd";
import {InfoCircleOutlined} from "@ant-design/icons";
import PropTypes from "prop-types";

import {formItemFullWidth} from "../../assets/styles";
import {createHardware} from "../../helpers/hardware";
import {messageType, showMessages} from "../../helpers/message";

const CarHardwareForm = ({openDrawer, hideDrawer, carID, handleRefreshHardware, existingHardwareType}) => {
    const abortControllerRef = useRef(new AbortController());
    const abortSignalRef = useRef(abortControllerRef.current.signal);
    const [loading, setLoading] = useState(false);

    const [hardwareType, setHardwareType] = useState(null);

    const [form] = Form.useForm();
    const mobileMode = window.innerWidth <= 768;

    const handleHardwareTypeChange = (e) => {
        setHardwareType(e.target.value);
        // Clear the ID field when it becomes disabled by selecting none as a type.
        if (e.target.value === 'none') {
            form.resetFields(['Vehicle ID']);
        }
    }

    const closeDrawer = () => {
        form.resetFields();
        hideDrawer();
    }

    const handleFinish = async (values) => {
        setLoading(true);
        const abortSignal = abortSignalRef.current;

        const type = values['Hardware Type'];
        const external_id = values['Vehicle ID'];

        // Handle if a user selects none when the car already has no hardware.
        if (existingHardwareType === 'None' && hardwareType === 'none'){
            showMessages('This car already has no assigned hardware.', messageType.warn);
            setLoading(false);
            return;
        }

        try {
            let response = await createHardware(carID, {type, external_id}, abortSignal);

            if (response) {
                setLoading(false);
                handleRefreshHardware();
                closeDrawer();
                showMessages('Hardware details have been updated.', messageType.success);
            }

        } catch (error) {
            setLoading(false);
            showMessages(error.message, messageType.error);
        }
    };

    return (
        <Drawer className="car-hardware-drawer"
                width={mobileMode ? '100%' : '45%'}
                title="Edit Hardware Details"
                placement="right"
                closable={true}
                onClose={closeDrawer}
                open={openDrawer}
        >
            <Form
                form={form}
                layout="vertical"
                name="car-hardware-form"
                onFinish={handleFinish}
            >
                <Row className="full-width-row">
                    <Col>
                        <Form.Item
                            name="Hardware Type"
                            label="Hardware: "
                            rules={[{type: 'string', required: true}]}
                        >
                        <Radio.Group name="hardware" onChange={handleHardwareTypeChange}>
                            <Space direction="vertical">
                                <Radio value="none">None</Radio>
                                <Radio value="astus">Astus Tag</Radio>
                            </Space>
                        </Radio.Group>
                        </Form.Item>
                    </Col>
                </Row>
                <Row className="full-width-row">
                    <Col>
                        <Form.Item
                            name="Vehicle ID"
                            label={
                                <div>
                                    <span>Astus Vehicle ID:</span>
                                    <Tooltip className="car-hardware-form-tooltip"
                                             title="It is mandatory to have a Vehicle ID if Hardware is assigned"
                                    >
                                        <InfoCircleOutlined/>
                                    </Tooltip>
                                </div>
                            }
                            rules={[{required: hardwareType === 'astus'}]}
                        >
                            <InputNumber
                                style={{...formItemFullWidth}}
                                type="number"
                                name="number"
                                disabled={hardwareType !== 'astus'}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row>
                    <Button className="car-hardware-cancel-btn" onClick={closeDrawer}>Cancel</Button>
                    <Button type="primary" htmlType="submit" loading={loading}>Save</Button>
                </Row>
            </Form>
        </Drawer>
    )
}

CarHardwareForm.propTypes = {
    openDrawer: PropTypes.bool.isRequired,
    hideDrawer: PropTypes.func.isRequired,
    carID: PropTypes.number.isRequired,
    handleRefreshHardware: PropTypes.func.isRequired,
    existingHardwareType: PropTypes.string.isRequired,
};

export default CarHardwareForm;
